import React, { useState, useCallback, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import Checkbox from "@/components/ui/checkbox";
import BadgeWithX from "@/components/ui/badge-with-x";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { cn } from "@/utils/utils";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import SearchInput from "@/components/ui/search-input";
import IconContainer, { iconSize, IconColor } from "@/components/ui/icon-container";
import InlineLoader from "@/components/ui/inline-loader";
import {
  EntityType,
  getEntityHook
} from "@/services/hooks/entities/useEntityType";
import { getFullName } from "@/entities/people/helpers";
import { PersonData } from "@/entities/people/types";
import { getPartnerContactFullName } from "@/pages/partners/contacts/helpers";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { EntityData } from "@/services/api/types";
import Badge from "@/components/ui/badge";
import { Close } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { debounce, isEmpty, some } from "lodash";
import { AddButtonFooter } from "@/components/ui/add-button-footer";
import { getTagColorClasses } from "@/utils/tags";
import {
  FIELD_CLASSNAME,
  FOCUS_CLASSNAME
} from "@/utils/fieldRendering/shared";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";

export type multiSelectVariant = "form" | "inline" | "topFields";

export type multiSelectOption = {
  label: string;
  value: string;
  icon?: React.ReactElement;
  badgeColor?: string;
};

export type multiSelectionOptions = multiSelectOption[];

export interface MultiSelectProps extends React.HTMLAttributes<HTMLDivElement> {
  options: multiSelectionOptions;
  onValueChange: (value: string[]) => void;
  disabled?: boolean;
  maxCount?: number;
  modalPopover?: boolean;
  className?: string;
  text?: string;
  showIconInChip?: boolean;
  selectedValues?: string[];
  placeholder?: string;
  icon?: JSX.Element;
  includeNoneOption?: boolean;
  readOnly?: boolean;
  placeholderClassName?: string;
  activeClassName?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  showSearch?: boolean;
  onSearch?: (newSearchVal: string) => void;
  isLoading?: boolean;
  entity?: EntityType;
  formatter?: (value: string) => string;
  searchPlaceholder?: string;
  popOverFooter?: JSX.Element | ((searchTerm: string) => JSX.Element);
  popoverClassName?: string;
  matchTriggerWidth?: boolean;
  variant?: multiSelectVariant;
  iconSize?: iconSize;
  iconColor?: IconColor;
  onCreateNew?: () => void;
  createNewLabel?: string;
  isEntityField?: boolean;
  displayType?: string;
  config?: any;
  alwaysShowChevron?: boolean;
  /** Shows a lone selection as plain text rather than a chip while closed. */
  singleValueAsText?: boolean;
}

const NONE_OPTION_VALUE = "none";

const isNoneValue = (value: string) => value === NONE_OPTION_VALUE;
const hasOnlyNoneValue = (values: string[]) =>
  values.length === 1 && isNoneValue(values[0]);

export const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      maxCount = 1,
      modalPopover = false,
      className,
      text,
      showIconInChip = false,
      selectedValues = [],
      placeholder,
      icon,
      includeNoneOption = false,
      readOnly,
      disabled = false,
      placeholderClassName,
      activeClassName,
      isOpen,
      onOpenChange,
      showSearch = false,
      onSearch,
      isLoading,
      entity,
      formatter,
      searchPlaceholder,
      popOverFooter,
      popoverClassName,
      matchTriggerWidth = false,
      variant = "form",
      iconSize = "5",
      iconColor = "secondary",
      onCreateNew,
      createNewLabel,
      isEntityField,
      displayType,
      config,
      alwaysShowChevron = false,
      singleValueAsText = false,
      ...props
    },
    ref
  ) => {
    const isNewFieldsUI = useNewFieldsUI();
    const [isPopoverOpen, setIsPopoverOpen] = useState(isOpen || false);
    const [isHovered, setIsHovered] = useState(false);
    const isInteractionDisabled = Boolean(readOnly || disabled);
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const setTriggerRef = useCallback(
      (node: HTMLDivElement | null) => {
        triggerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref]
    );
    useEffect(() => {
      setIsPopoverOpen(isOpen ?? false);
    }, [isOpen]);
    const onPopoverOpen = useCallback(
      (open: boolean) =>
        onOpenChange ? onOpenChange(open) : setIsPopoverOpen(open),
      [onOpenChange]
    );
    const [sortedOptions, setSortedOptions] = useState(options);
    const [displayedOptions, setDisplayedOptions] = useState(options);
    const [searchValue, setSearchValue] = useState("");
    const [isClearAllActive, setIsClearAllActive] = useState(false);

    const { t, i18n } = useTranslation();

    // Entity fetching for selected values not in options
    const useEntityQuery = getEntityHook(entity);
    const entityQuery = useEntityQuery
      ? useEntityQuery(
          entity && selectedValues.length > 0 ? selectedValues : []
        )
      : { entities: [], isSuccess: false };

    const { entities: selectedEntities = [] } = entityQuery;

    const getEntityDisplayName = (
      entityData: EntityData,
      entityType: EntityType
    ) => {
      if (!entityData) return "";
      if (entityType === EntityType.PERSON) {
        return getFullName(entityData as PersonData);
      } else if (entityType === EntityType.USER) {
        return entityData.displayName || "";
      } else if (entityType === EntityType.PARTNER_CONTACT) {
        return (
          getPartnerContactFullName(entityData as PartnerContactData) ||
          t("unknown")
        );
      } else {
        return entityData?.name || "";
      }
    };

    const allOptions = React.useMemo(() => {
      if (!includeNoneOption) return options;

      const shouldShowNoneOption = !hasOnlyNoneValue(selectedValues);

      if (!shouldShowNoneOption) {
        return options;
      }

      const noneOption = {
        label: t("noneOption"),
        value: NONE_OPTION_VALUE,
        icon: undefined
      };

      return [noneOption, ...options];
    }, [options, includeNoneOption, selectedValues, t]);

    const getLabelForValue = (value: string) => {
      const optionInList = allOptions.find((o) => o.value === value);
      if (optionInList && optionInList.label) {
        return optionInList.label;
      }
      // If not found in options and we have entity support, try to get from fetched entities
      if (entity && selectedEntities.length > 0) {
        const entityData = selectedEntities.find((e) => e?.id === value);
        if (entityData) {
          return getEntityDisplayName(entityData, entity);
        }
      }
      if (formatter) {
        const formattedValue = formatter(value);
        return formattedValue;
      }
      return value;
    };

    useEffect(() => {
      if (isPopoverOpen && !onSearch) return;
      setSortedOptions(options);
      setDisplayedOptions(options);
    }, [options, isPopoverOpen, onSearch]);

    const debouncedSearch = useCallback(
      debounce((searchTerm: string) => {
        if (onSearch) {
          onSearch(searchTerm);
          return;
        }

        if (searchTerm.trim() === "") {
          setDisplayedOptions(sortedOptions); // Reset to sorted options
          return;
        }

        const filtered = sortedOptions.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

        setDisplayedOptions(filtered);
      }, 300),
      [sortedOptions, onSearch]
    );

    useEffect(() => {
      return () => {
        debouncedSearch.cancel();
      };
    }, [debouncedSearch]);

    useEffect(() => {
      const hasSelectedInDisplayed = some(selectedValues, (selectedValue) =>
        some(displayedOptions, (option) => option.value === selectedValue)
      );

      setIsClearAllActive(hasSelectedInDisplayed);
    }, [displayedOptions, selectedValues]);

    const handleSearchChange = (value: string) => {
      setSearchValue(value);
      debouncedSearch(value);
    };

    const toggleOption = (option: string) => {
      if (isInteractionDisabled) return;
      if (option === NONE_OPTION_VALUE) {
        onValueChange([NONE_OPTION_VALUE]);
        return;
      }
      const newSelectedValues = selectedValues.includes(NONE_OPTION_VALUE)
        ? [option]
        : selectedValues.includes(option)
          ? selectedValues.filter((value) => value !== option)
          : [...selectedValues, option];
      onValueChange(newSelectedValues);
    };

    const handleOpenChange = (open: boolean) => {
      if (isInteractionDisabled) return;
      if (open) {
        if (!onSearch) {
          const newSortedOptions = [...allOptions].sort((a, b) => {
            if (a.value === NONE_OPTION_VALUE) return -1;
            if (b.value === NONE_OPTION_VALUE) return 1;
            const aSelected = selectedValues.includes(a.value);
            const bSelected = selectedValues.includes(b.value);
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return 0;
          });
          setSortedOptions(newSortedOptions);
          setDisplayedOptions(newSortedOptions);
        }
        setSearchValue("");

        if (onSearch) {
          onSearch("");
        }
      }
      onPopoverOpen(open);
    };

    const handleTriggerKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement>
    ) => {
      props.onKeyDown?.(event);
      if (
        isInteractionDisabled ||
        event.defaultPrevented ||
        event.target !== event.currentTarget
      ) {
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
        return;
      }
      if (
        !isPopoverOpen &&
        (event.key === "Backspace" || event.key === "Delete")
      ) {
        const removableValue = [...selectedValues]
          .reverse()
          .find((value) => !isNoneValue(value));
        if (removableValue) {
          event.preventDefault();
          toggleOption(removableValue);
        }
      }
    };

    const sharedTriggerProps = {
      ref: setTriggerRef,
      role: "combobox",
      tabIndex:
        isInteractionDisabled || (modalPopover && isPopoverOpen) ? -1 : 0,
      "aria-disabled": isInteractionDisabled || undefined,
      "aria-expanded": isPopoverOpen,
      "aria-haspopup": "listbox" as const,
      onKeyDown: handleTriggerKeyDown,
      // A modal Radix popover disables pointer events outside its content.
      // Selected badges live in the trigger, so keep their controls interactive.
      style: modalPopover ? { pointerEvents: "auto" as const } : undefined
    };

    const renderLoading = () => (
      <div className="w-full flex items-center justify-center py-4">
        <InlineLoader label={t("loading")} />
      </div>
    );

    // Don't show any badges if only "none" is selected (represents empty state)
    const displayValues = hasOnlyNoneValue(selectedValues)
      ? []
      : selectedValues;

    const clearAllSelected = () => {
      // Nothing closes an open popover when readOnly/disabled flips, so the
      // guard belongs on the action, not only on whether the control renders.
      if (isInteractionDisabled || !isClearAllActive) return;

      // Every selected value, not only the displayed ones: with a search term
      // active the previous filtered clear left every hidden selection in
      // place, so "Clear all" silently cleared a subset.
      onValueChange([]);
    };

    const renderBadges = () => {
      const DISPLAY_LIMIT = 2;
      // In the narrower redesigned row a squeezed chip wraps its text onto a
      // second line inside the fixed h-6 box.
      const chipClassName = isNewFieldsUI ? "shrink-0" : undefined;
      const chipTextClassName = isNewFieldsUI ? "whitespace-nowrap" : undefined;

      if (isPopoverOpen) {
        return displayValues.map((value) => {
          const option: multiSelectOption | undefined = allOptions.find(
            (o) => o.value === value
          );
          const displayLabel = getLabelForValue(value);
          return (
            <BadgeWithX
              key={value}
              text={displayLabel}
              icon={showIconInChip && option?.icon ? option.icon : undefined}
              readOnly={isInteractionDisabled}
              showX={!isInteractionDisabled}
              onXClick={() => toggleOption(value)}
              removeControlFocusable={false}
              textClassName={chipTextClassName}
              className={cn(
                option?.badgeColor && getTagColorClasses(option?.badgeColor),
                isInteractionDisabled && "cursor-default",
                chipClassName
              )}
            />
          );
        });
      } else {
        const badges = [];
        const totalSelected = displayValues.length;
        if (singleValueAsText && totalSelected === 1) {
          return (
            <Text as="span" variant="body" className="truncate">
              {getLabelForValue(displayValues[0])}
            </Text>
          );
        }
        const hasMore = totalSelected > DISPLAY_LIMIT;
        const showCount = hasMore ? DISPLAY_LIMIT : totalSelected;
        for (let i = 0; i < showCount; i++) {
          const value = displayValues[i];
          const option: multiSelectOption | undefined = allOptions.find(
            (o) => o.value === value
          );
          const displayLabel = getLabelForValue(value);
          badges.push(
            <BadgeWithX
              key={value}
              text={displayLabel}
              icon={showIconInChip && option?.icon ? option.icon : undefined}
              readOnly={isInteractionDisabled}
              showX={false}
              onXClick={() => toggleOption(value)}
              textClassName={chipTextClassName}
              className={cn(
                option?.badgeColor && getTagColorClasses(option?.badgeColor),
                isInteractionDisabled && "cursor-default",
                chipClassName
              )}
            />
          );
        }
        if (hasMore) {
          const plusText = `+ ${totalSelected - DISPLAY_LIMIT}`;
          const hiddenItems = displayValues.slice(DISPLAY_LIMIT);
          const hiddenNames = hiddenItems
            .map((value) => getLabelForValue(value))
            .join(", ");
          badges.push(
            <BadgeWithX
              key="plus-badge"
              text={plusText}
              readOnly={true}
              showX={false}
              onXClick={() => {}}
              textClassName={chipTextClassName}
              className={cn("bg-surface-elevation2", chipClassName)}
              tooltip={hiddenNames}
            />
          );
        }

        return badges;
      }
    };

    const renderLegacyPreviewTrigger = () => (
      <div
        {...props}
        {...sharedTriggerProps}
        style={{
          ...props.style,
          ...sharedTriggerProps.style
        }}
        className={cn(
          "p-2 rounded-lg cursor-pointer w-full min-w-0 min-h-9",
          "flex items-center justify-between bg-surface-field-default",
          isPopoverOpen &&
            "border-[0.5px] border-border-field-focus bg-surface-focus-selected",
          !isPopoverOpen &&
            "hover:bg-surface-hover-strong border-[0.5px] border-transparent",
          !isInteractionDisabled &&
            (isPopoverOpen ? "shadow-field-focus" : "shadow-field"),
          isInteractionDisabled &&
            "hover:!bg-transparent cursor-default !opacity-100 !bg-surface-fields-disabled",
          className
        )}
        onMouseEnter={(event) => {
          props.onMouseEnter?.(event);
          setIsHovered(true);
        }}
        onMouseLeave={(event) => {
          props.onMouseLeave?.(event);
          setIsHovered(false);
        }}
      >
        <div
          className={cn(
            "flex items-center gap-2 min-w-0 flex-1",
            !isPopoverOpen && "overflow-hidden"
          )}
        >
          {!isEmpty(displayValues) ? (
            <div className="flex flex-wrap items-center gap-1 min-w-0 overflow-hidden">
              {renderBadges()}
            </div>
          ) : (
            <>
              {/* Base rendered a bare span; this branch is the flag-off
                  rendering, so it keeps base's markup exactly. */}
              {!isEmpty(text) && (
                <span className="text-base">
                  {text && i18n.exists(text) ? t(text) : text}
                </span>
              )}
              {icon && <IconContainer color="secondary">{icon}</IconContainer>}
              <Text
                variant="bodyTertiary"
                className={cn(
                  "text-base h-full flex items-center",
                  placeholderClassName
                )}
              >
                {placeholder}
              </Text>
            </>
          )}
        </div>
        <ChevronDown
          className={cn(
            "size-4 text-secondary flex-shrink-0 transition-all duration-200",
            alwaysShowChevron || isHovered || isPopoverOpen
              ? "opacity-100"
              : "opacity-0",
            isPopoverOpen && "rotate-180"
          )}
        />
      </div>
    );

    // The redesign flattens the preview row: no surface until focused, chips on
    // one line until the popover opens, clear-all beside the chevron. The
    // trigger div keeps the combobox semantics, and clear-all is a real
    // IconButton that takes a tab stop only while the popover is open, so it
    // never displaces the trigger in the closed row's tab order. Backspace and
    // Delete on the closed trigger remove the last chip — they are not a
    // keyboard route to clear-all.
    const renderNewPreviewTrigger = () => (
      <div
        {...props}
        {...sharedTriggerProps}
        style={{
          ...props.style,
          ...sharedTriggerProps.style
        }}
        className={cn(
          "px-2 py-1 rounded-lg w-full min-w-0",
          "flex items-center justify-between transition-all duration-200 ease-in-out",
          isPopoverOpen
            ? "min-h-9 border-[0.5px] border-border-field-focus bg-surface-focus-selected cursor-pointer"
            : "h-9 border-[0.5px] border-transparent overflow-hidden",
          !isPopoverOpen &&
            !isInteractionDisabled &&
            "hover:bg-surface-hover-soft cursor-pointer",
          isInteractionDisabled && "cursor-default",
          className
        )}
        onMouseEnter={(event) => {
          props.onMouseEnter?.(event);
          setIsHovered(true);
        }}
        onMouseLeave={(event) => {
          props.onMouseLeave?.(event);
          setIsHovered(false);
        }}
      >
        <div
          className={cn(
            "flex items-center gap-2 min-w-0 flex-1 overflow-hidden",
            // `disabled`, not `isInteractionDisabled`: a read-only field holds a
            // valid value that simply cannot be edited here, so its text stays
            // normal. Only a disabled one dims. Matches SingleSelect and the
            // table in PreviewFields.stories.tsx.
            //
            // Reaches the descendants, not just this box: every child sets its
            // own colour — the value `Text` is `body` (text-primary), the
            // placeholder is `bodyTertiary` (text-tertiary), and the chips
            // carry theirs — and an element's own colour class beats an
            // inherited one, so a disabled row kept rendering at full contrast.
            // Same selector SingleSelect uses for its icon slots.
            disabled && "[&_*]:!text-body-disabled"
          )}
        >
          {!isEmpty(displayValues) ? (
            <div
              className={cn(
                "flex items-center gap-1 min-w-0",
                isPopoverOpen ? "flex-wrap" : "overflow-hidden"
              )}
            >
              {renderBadges()}
            </div>
          ) : (
            <>
              {!isEmpty(text) && (
                <Text as="span" variant="body">
                  {text && i18n.exists(text) ? t(text) : text}
                </Text>
              )}
              {icon && <IconContainer color="tertiary">{icon}</IconContainer>}
              <Text
                variant="bodyTertiary"
                className={cn(
                  "text-base h-full flex items-center",
                  placeholderClassName
                )}
              >
                {placeholder}
              </Text>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isClearAllActive && !isInteractionDisabled && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  type="button"
                  aria-label={t("multiSelect.clearAll")}
                  data-test="multiselect-clear-control"
                  tabIndex={isPopoverOpen ? undefined : -1}
                  onClick={(clickEvent) => {
                    clickEvent.stopPropagation();
                    clearAllSelected();
                  }}
                  size="xs"
                  variant="iconNoBg"
                  iconClassName="[&_svg]:size-4"
                  className={cn(
                    "transition-all duration-200",
                    // w-0 collapses the content box but not the button's
                    // padding, so without this the hidden control keeps a live
                    // hit area beside the chevron — a click there wiped every
                    // displayed selection with nothing on screen to explain it.
                    isPopoverOpen
                      ? "opacity-100 pointer-events-auto"
                      : "opacity-0 w-0 pointer-events-none"
                  )}
                >
                  <Close />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>{t("multiSelect.clearAll")}</TooltipContent>
            </Tooltip>
          )}
          {!isInteractionDisabled && (
            <IconContainer
              color="tertiary"
              size="5"
              className={cn(
                "transition-all duration-200 rotate-0",
                alwaysShowChevron || isHovered || isPopoverOpen
                  ? "opacity-100"
                  : "opacity-0",
                isPopoverOpen && "!text-primary rotate-180"
              )}
            >
              <ChevronDown size={20} strokeWidth={1.5} />
            </IconContainer>
          )}
        </div>
      </div>
    );

    // Which triggers actually carry a clear control: the form one (unconditional)
    // and the redesigned inline one. `topFields` has none in either flag state,
    // and flag-off `inline` renders the legacy trigger, which has none either —
    // so keying the popover fallback on the flag alone left flag-on `topFields`
    // with no way to clear at all, only chip-by-chip removal.
    // `!modalPopover` is load-bearing, not a refinement: a modal popover puts a
    // Radix focus trap around the portalled content, and the trigger's clear
    // control sits outside it — and takes tabIndex -1 while the popover is open
    // besides. Counting it as "the trigger has a clear control" there suppressed
    // the in-popover option and left no keyboard path to clear at all.
    const hasTriggerClearControl =
      isNewFieldsUI &&
      !modalPopover &&
      (variant === "form" || variant === "inline");

    const renderTrigger = () => {
      if (variant === "topFields") {
        return (
          <div
            {...props}
            {...sharedTriggerProps}
            style={{
              ...props.style,
              ...sharedTriggerProps.style
            }}
            className={cn(
              "p-2 rounded-lg cursor-pointer w-full min-w-0",
              "flex items-center justify-between transition-all duration-200 ease-in-out",
              isPopoverOpen
                ? "border-[1px] border-primary bg-surface-focus-selected"
                : "hover:bg-surface-hover-strong border-[0.5px] border-transparent",
              isInteractionDisabled &&
                "hover:!bg-transparent cursor-default !opacity-100 !bg-surface-fields-disabled",
              className
            )}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
              {!isEmpty(displayValues) ? (
                <div className="flex flex-wrap items-center gap-1 min-w-0 overflow-hidden">
                  {renderBadges()}
                </div>
              ) : (
                <>
                  {icon && (
                    <IconContainer color="secondary">{icon}</IconContainer>
                  )}
                  <Text
                    variant="bodyTertiary"
                    className={cn(
                      "h-full flex items-center",
                      placeholderClassName
                    )}
                  >
                    {placeholder}
                  </Text>
                </>
              )}
            </div>
            <IconContainer
              color="secondary"
              size="5"
              className={cn(
                "transition-all duration-200 rotate-0 flex-shrink-0",
                isPopoverOpen && "!text-primary rotate-180"
              )}
            >
              <ChevronDown />
            </IconContainer>
          </div>
        );
      }
      if (variant === "inline") {
        return isNewFieldsUI
          ? renderNewPreviewTrigger()
          : renderLegacyPreviewTrigger();
      }

      // Form variant (original Button implementation)
      return (
        <div
          {...props}
          {...sharedTriggerProps}
          style={{
            ...props.style,
            ...sharedTriggerProps.style
          }}
          className={cn(
            "pl-3 pr-2 py-1 bg-surface-field-default",
            isPopoverOpen ? "min-h-9" : "h-9",
            !isPopoverOpen && "max-h-18 overflow-hidden",
            isPopoverOpen && activeClassName,
            FIELD_CLASSNAME,
            className,
            isPopoverOpen && FOCUS_CLASSNAME
          )}
        >
          <div className="flex justify-between items-center w-full">
            <div className="flex flex-wrap items-center gap-1 flex-1 mr-2">
              {/* The form trigger is the one variant that shares a single
                  branch across both flag states, so the redesigned Text
                  treatment is gated — ungated it reached every DataTable
                  filter and questionnaire multiSelect, outside this feature. */}
              {!isEmpty(text) &&
                (isNewFieldsUI ? (
                  <Text as="span" variant="body">
                    {text && i18n.exists(text) ? t(text) : text}
                  </Text>
                ) : (
                  <span className="text-base">
                    {text && i18n.exists(text) ? t(text) : text}
                  </span>
                ))}
              {icon && <IconContainer color="secondary">{icon}</IconContainer>}
              {!isEmpty(placeholder) && isEmpty(selectedValues) && (
                <Text
                  variant="bodyTertiary"
                  className={cn("text-base", placeholderClassName)}
                >
                  {placeholder}
                </Text>
              )}
              {renderBadges()}
            </div>
            <div className="flex gap-2 items-center">
              {/* Gated for the same reason as the label above: this trigger is
                  shared across both flag states. Flag-off keeps base's markup —
                  an IconButton asChild around an inert span with a native
                  title, which is not focusable and carries no Radix tooltip. */}
              {isNewFieldsUI ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton
                      type="button"
                      aria-label={t("multiSelect.clearAll")}
                      data-test="multiselect-clear-control"
                      tabIndex={
                        isPopoverOpen &&
                        isClearAllActive &&
                        !isInteractionDisabled
                          ? undefined
                          : -1
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        clearAllSelected();
                      }}
                      size="xs"
                      className={cn(
                        "transition-all duration-200 ease-in-out",
                        isPopoverOpen &&
                          isClearAllActive &&
                          !isInteractionDisabled
                          ? "opacity-100 pointer-events-auto"
                          : "opacity-0 w-0 pointer-events-none"
                      )}
                      variant="iconNoBg"
                      iconClassName="[&_svg]:size-4"
                    >
                      <Close />
                    </IconButton>
                  </TooltipTrigger>
                  <TooltipContent>{t("multiSelect.clearAll")}</TooltipContent>
                </Tooltip>
              ) : (
                <IconButton
                  asChild
                  onClick={(event) => {
                    event.stopPropagation();
                    clearAllSelected();
                  }}
                  size="xs"
                  className={cn(
                    "transition-all duration-200 ease-in-out opacity-0 w-0 pointer-events-none",
                    isPopoverOpen &&
                      isClearAllActive &&
                      "pointer-events-auto opacity-100 w-fit"
                  )}
                  variant="iconNoBg"
                  iconClassName="[&_svg]:size-4"
                >
                  <span
                    aria-hidden="true"
                    data-test="multiselect-clear-control"
                    title={
                      isPopoverOpen && isClearAllActive
                        ? t("multiSelect.clearAll")
                        : undefined
                    }
                  >
                    <Close />
                  </span>
                </IconButton>
              )}
              <div className="flex-shrink-0">
                <IconContainer
                  color={iconColor}
                  size={iconSize}
                  className={cn(
                    "transition-all duration-200 rotate-0",
                    isPopoverOpen && "!text-primary rotate-180",
                    isInteractionDisabled && "!text-disabled"
                  )}
                >
                  <ChevronDown />
                </IconContainer>
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={handleOpenChange}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
        <PopoverContent
          padding={4}
          className={cn(
            "flex flex-col gap-2",
            variant === "inline" || (variant === "form" && matchTriggerWidth)
              ? "w-[--radix-popper-anchor-width] min-w-[240px]"
              : variant === "topFields" && matchTriggerWidth
                ? "w-fit min-w-[max(240px,var(--radix-popper-anchor-width))]"
                : "w-fit min-w-[240px]",
            popoverClassName
          )}
          align="start"
          onEscapeKeyDown={() => onPopoverOpen(false)}
          onInteractOutside={(event) => {
            // Removing a trigger badge changes the value; it is not an outside dismissal.
            if (triggerRef.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command className="w-full">
            <div className="flex flex-col gap-2 max-h-[300px]">
              {(showSearch || allOptions.length > 10) && (
                <div className="sticky top-0 bg-surface-elevation2 z-10">
                  <SearchInput
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder={searchPlaceholder || t("search")}
                    hideIcon
                    clearButtonSize="xs"
                    clearButtonColor="secondary"
                    autoFocus
                  />
                </div>
              )}
              {/* Base offered Clear all here, for every variant. The redesign
                  replaced it with a control on the trigger — but only the form
                  trigger and the redesigned inline one have that control, so
                  flag-off `inline` and `topFields` would be left with no way to
                  clear at all. Restored for the flag-off path only, so `main`'s
                  behaviour is preserved without doubling up under the flag. */}
              {!hasTriggerClearControl &&
                isClearAllActive &&
                !isInteractionDisabled && (
                  <button
                    type="button"
                    onClick={clearAllSelected}
                    className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-surface-hover-soft focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary [&>svg]:size-4"
                    data-test="multiselect-clear-all-option"
                  >
                    <Close />
                    <Text variant="body">{t("multiSelect.clearAll")}</Text>
                  </button>
                )}
              <CommandList className="overflow-y-auto">
                <CommandGroup>
                  {isLoading ? (
                    renderLoading()
                  ) : displayedOptions.length > 0 ? (
                    displayedOptions.map((option) => {
                      const isSelected = selectedValues.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          onSelect={() => toggleOption(option.value)}
                          className="w-full cursor-pointer py-2 gap-2 pl-2 pr-3 min-h-9 flex mr-3 hover:!bg-surface-hover-soft active:!bg-surface-pressed rounded-lg transition-all duration-200 ease-in-out"
                        >
                          <Checkbox checked={isSelected} className="m-1" />
                          <div className="flex gap-2 items-center">
                            {option.icon && option.icon}
                            {option?.badgeColor ? (
                              <Badge
                                text={option.label}
                                className={cn(
                                  getTagColorClasses(option?.badgeColor)
                                )}
                              />
                            ) : (
                              <Text variant="body">{option.label}</Text>
                            )}
                          </div>
                        </CommandItem>
                      );
                    })
                  ) : (
                    <div className="px-2 py-4 text-center">
                      <Text variant="bodyTertiary">
                        {t("common.noResults", "No results found")}
                      </Text>
                    </div>
                  )}
                </CommandGroup>
              </CommandList>
            </div>
          </Command>
          {(popOverFooter || (onCreateNew && createNewLabel)) && (
            <>
              {popOverFooter &&
                (typeof popOverFooter === "function"
                  ? popOverFooter(searchValue)
                  : popOverFooter)}
              {onCreateNew && createNewLabel && !popOverFooter && (
                <AddButtonFooter
                  buttonText={createNewLabel}
                  onClick={() => {
                    onPopoverOpen(false);
                    onCreateNew();
                  }}
                />
              )}
            </>
          )}
        </PopoverContent>
      </Popover>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
